// vg-binding: P7.D-49
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/features/earnings/TransactionsTable.tsx

import { cn } from '@printway/ui';
import { formatCents } from '@printway/shared/currency';

import { useTranslation } from '../../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../../lib/ui/statusPill.js';
import type { TransactionRow } from '../wallet/api.js';

const STATUS_PILL: Record<string, string> = {
  pending: STATUS_PILL_CLASS.progress,
  available: STATUS_PILL_CLASS.success,
  paid_out: STATUS_PILL_CLASS.info,
  disputed: STATUS_PILL_CLASS.danger,
  frozen: STATUS_PILL_CLASS.done,
};

interface Props {
  rows: TransactionRow[];
  isLoading?: boolean;
}

function formatTs(ts: number | null): string {
  if (typeof ts !== 'number') return '—';
  return new Date(ts).toLocaleString(undefined);
}

export function TransactionsTable({ rows, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('wallet');
  if (isLoading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-2 py-6">
        {Array.from({ length: 6 }).map((_v, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-md border border-pw-border bg-pw-surface"
          />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
        {t('transactions.empty')}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto text-sm">
        <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
          <tr>
            <th className="px-3 py-2 text-left">{t('transactions.headerTime')}</th>
            <th className="px-3 py-2 text-left">{t('transactions.headerType')}</th>
            <th className="px-3 py-2 text-right">{t('transactions.headerAmount')}</th>
            <th className="px-3 py-2 text-left">{t('transactions.headerStatus')}</th>
            <th className="px-3 py-2 text-left">{t('transactions.headerOrder')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.ledger_id}
              data-testid={`tx-row-${r.ledger_id}`}
              className="border-b border-pw-border"
            >
              <td className="px-3 py-2 text-xs text-pw-text-muted">{formatTs(r.created_at)}</td>
              <td className="px-3 py-2">{r.type}</td>
              <td className="px-3 py-2 text-right font-medium text-pw-text">{formatCents(r.amount_cents)}</td>
              <td className="px-3 py-2">
                <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold', STATUS_PILL[r.status] ?? 'bg-pw-surface text-pw-text-muted')}>
                  {r.status}
                </span>
              </td>
              <td className="px-3 py-2 font-mono text-xs">{r.order_id ? r.order_id.slice(-8) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TransactionsTable;
